<script>
	import { buttonLoading, loading } from "src/store/store";
	import { onDestroy } from "svelte";

	import CustomButton from "../CustomButton/CustomButton.svelte";
	import CustomSearch from "../CustomSearch/CustomSearch.svelte";
	import CustomSelect from "../CustomSelect/CustomSelect.svelte";
	import CustomTextArea from "../CustomTextArea/CustomTextArea.svelte";
	import ErrorMessageHandler from "../ErrorMessageHandler/ErrorMessageHandler.svelte";

	// Props for controlling the modal's state and content
	export let open = false;
	export let toggle = () => {};
	export let handleSubmit = () => {};

	// Props for customizing the modal's appearance
	export let header = "Confirm";
	export let bodyText = "";
	export let extraText = "";
	export let buttonText = "Confirm";
	export let buttonColor = "primary";
	export let outline = true;
	export let err = "";

	// Props for the optional dropdown functionality
	export let isDropdownEnabled = false;
	export let options = [];
	export let action = "";
	export let selectedOption = "";
	export let otherReason = "";

	// Props for the CustomSearch dropdown functionality
	export let showDropdown = false;
	export let dropdownOptions = [];
	export let dropdownPlaceholder = "Select a reason for rejection";
	export let dropdownErrorText = "Please select a reason";
	export let selectedDropdown = null;
	export let reasonHeader = `Enter reason for ${action}ing car`;
	let dropdownTouched = false;

	export let icon = false;

	// Bootstrap-style animation state
	let showModal = false;
	let isClosing = false;
	let closingTimeout;

	// A reactive variable to determine if the confirm button should be disabled.
	$: disableConfirmButton =
		(isDropdownEnabled &&
			(!selectedOption ||
				(selectedOption === "Others" && !otherReason))) ||
		(showDropdown && !selectedDropdown);

	let load;
	let disableConfirmButton = false;

	loading.subscribe((value) => {
		load = value;
	});
	buttonLoading.subscribe((value) => {
		load = value;
	});

	// Enhanced toggle function that mimics sveltestrap behavior
	function enhancedToggle() {
		if (isClosing) return;

		if (open) {
			// Start closing animation
			isClosing = true;
			showModal = false;

			// Wait for fade transition to complete
			closingTimeout = setTimeout(() => {
				toggle();
				isClosing = false;
				closingTimeout = null;
			}, 300);
		} else {
			toggle();
		}
	}

	// handle submit for both dropdown types and non-dropdown modals
	function handleSubmitWrapper() {
		if (showDropdown) {
			dropdownTouched = true;
			if (!selectedDropdown) return;
			handleSubmit(selectedDropdown.value, otherReason);
		} else {
			handleSubmit();
		}
	}

	// Handles the "Enter" key press to submit the modal
	function handleKeyDown(event) {
		if (event.key === "Enter" && !disableConfirmButton) {
			if (showDropdown) {
				handleSubmitWrapper();
			} else {
				handleSubmit();
			}
		}
	}

	// Mimic Bootstrap's modal show behavior
	$: if (open && !showModal && !isClosing) {
		// Small delay to trigger fade animation
		setTimeout(() => {
			showModal = true;
		}, 10);
	}

	// Reset showModal when closed
	$: if (!open) {
		showModal = false;
		isClosing = false;
		if (closingTimeout) {
			clearTimeout(closingTimeout);
			closingTimeout = null;
		}
	}

	// This reactive block manages side effects when the modal opens or closes.
	$: if (typeof window !== "undefined") {
		if (open) {
			document.body.classList.add("modal-open");
			document.addEventListener("keydown", handleKeyDown);
		} else {
			document.body.classList.remove("modal-open");
			document.removeEventListener("keydown", handleKeyDown);
		}
	}

	// The onDestroy lifecycle function ensures cleanup.
	onDestroy(() => {
		if (typeof window !== "undefined") {
			document.body.classList.remove("modal-open");
			document.removeEventListener("keydown", handleKeyDown);
		}
		if (closingTimeout) {
			clearTimeout(closingTimeout);
		}
	});
</script>

{#if open}
	<div class="modal-backdrop fade" class:show={showModal} />

	<dialog
		class="modal fade"
		class:show={showModal}
		style="display: block;"
		tabindex="-1"
		on:click={(e) => {
			if (e.currentTarget === e.target) {
				enhancedToggle();
			}
		}}
		on:keydown={(e) => {
			if (
				(e.key === "Escape" || e.key === "Enter") &&
				e.currentTarget === e.target
			) {
				enhancedToggle();
			}
		}}
	>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<b>{header}</b>
					</h5>
					<button
						type="button"
						class="btn-close"
						on:click={enhancedToggle}
						aria-label="Close"
					/>
				</div>
				<div class="modal-body">
					<ErrorMessageHandler message={err} />
					<div class="d-flex">
						{#if icon}
							<div>
								<img
									src="/assets/icon-alert-filled-red.svg"
									alt="icon-svg"
									class="sidebarlogo me-2"
								/>
							</div>
						{/if}
						<div>{bodyText}</div>
					</div>
					{#if extraText}
						<br />
						<div>
							<span>{extraText}</span>
						</div>
					{/if}

					{#if isDropdownEnabled}
						<CustomSelect
							defaultOption="Select a reason"
							{options}
							bind:value={selectedOption}
							disableFirstOption
						/>
					{/if}
					{#if selectedOption === "Others"}
						<div class="my-3">
							<span>Reason <span style="color: red">*</span></span
							>
							<CustomTextArea
								bind:value={otherReason}
								placeholder={`Enter reason for ${action}ing car`}
								wordCount={false}
							/>
						</div>
					{/if}

					{#if showDropdown}
						<div style="margin-top: 24px;">
							<label
								class="mb-1"
								style="font-weight: 500;"
								for="reject-reason"
							>
								Reason<span style="color: #B3261E;">*</span>
							</label>
							<CustomSearch
								items={dropdownOptions}
								bind:value={selectedDropdown}
								placeholder={dropdownPlaceholder}
								error={dropdownTouched && !selectedDropdown}
								errorText={dropdownErrorText}
								isSearchable={false}
							/>
						</div>
					{/if}
					{#if selectedDropdown?.value === "Others"}
						<div class="my-3">
							<span
								>{reasonHeader}
								<span style="color: red">*</span></span
							>
							<CustomTextArea
								bind:value={otherReason}
								placeholder={`Enter reason`}
								wordCount={false}
							/>
						</div>
					{/if}
				</div>
				<div class="modal-footer">
					<CustomButton
						outline
						color="secondary"
						buttonText="Cancel"
						on:click={enhancedToggle}
						onKeyDown={handleKeyDown}
					/>
					<CustomButton
						{buttonText}
						on:click={showDropdown
							? handleSubmitWrapper
							: handleSubmit}
						color={buttonColor}
						{outline}
						{load}
						disabled={disableConfirmButton || load}
						onKeyDown={handleKeyDown}
					/>
				</div>
			</div>
		</div>
	</dialog>
{/if}

<style>
	/* Reset the native <dialog> UA styles (white background, border, padding) so the full-screen wrapper stays transparent and the backdrop shows through, like Bootstrap's div.modal. */
	dialog.modal {
		background: transparent;
		border: 0;
		padding: 0;
		margin: 0;
		color: inherit;
	}
</style>
